import './MembershipBenefitsTable.css';

import { Zap, Sparkles, Cake, Gift, Calendar, Handshake, Users, ShieldCheck, Check } from 'lucide-react';
import type { MembershipTypeRow } from '../api/membership.api';
import { resolveThemeToken } from '../themeTokens';

type MembershipBenefitsTableProps = {
  tiers: MembershipTypeRow[];
  onEditBenefits?: () => void;
};

const CANONICAL_BENEFITS: ReadonlyArray<{ label: string; icon: React.ReactNode }> = [
  { label: 'Priority queue skip', icon: <Zap size={14} /> },
  { label: 'Points multiplier', icon: <Sparkles size={14} /> },
  { label: 'Birthday bonus', icon: <Cake size={14} /> },
  { label: 'Free service / month', icon: <Gift size={14} /> },
  { label: 'Early booking access', icon: <Calendar size={14} /> },
  { label: 'Partner offers', icon: <Handshake size={14} /> },
  { label: 'Referral bonus', icon: <Users size={14} /> },
  { label: 'Dedicated support', icon: <ShieldCheck size={14} /> },
];

const parsePerks = (value?: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => String(item || '').trim()).filter(Boolean);
  } catch (_error) {
    return [];
  }
};

const includesBenefit = (perks: string[], benefit: string): boolean => {
  const normalizedBenefit = benefit.toLowerCase();
  return perks.some((perk) => perk.toLowerCase().includes(normalizedBenefit));
};

const formatPointsMultiplier = (value?: number): string => {
  const safeValue = Number(value || 0);
  if (!safeValue) return '1×';
  const multiplier = 100 / safeValue;
  return `${Number.isInteger(multiplier) ? multiplier : Number(multiplier.toFixed(1))}×`;
};

const MembershipBenefitsTable = ({ tiers, onEditBenefits }: MembershipBenefitsTableProps) => {
  const orderedTiers = [...tiers].sort((left, right) => (left.tierLevel || 0) - (right.tierLevel || 0));

  if (!orderedTiers.length) {
    return null;
  }

  return (
    <article className="appointments-v2-card membership-benefits">
      <div className="appointments-v2-card-header membership-benefits__header">
        <div>
          <h3>Benefits per tier</h3>
          <p className="appointments-v2-empty-copy">Compare which loyalty promises are active across your current tiers.</p>
        </div>
        {onEditBenefits ? (
          <button type="button" className="btn ghost" onClick={onEditBenefits}>Edit benefits</button>
        ) : null}
      </div>
      <div className="appointments-v2-card-body membership-benefits__body">
        <div className="membership-benefits__tableWrap">
          <table className="appointments-v2-table membership-benefits__table">
            <thead>
              <tr>
                <th>Benefit</th>
                {orderedTiers.map((tier) => {
                  const theme = resolveThemeToken(tier.themeColorToken);
                  return (
                    <th
                      key={tier.membershipTypeUuid}
                      style={{
                        background: `${theme.swatchHex}1a`,
                        color: theme.swatchHex,
                        borderBottom: `2px solid ${theme.swatchHex}55`,
                      }}
                    >
                      <span className="membership-benefits__tier-header">
                        <span className="membership-benefits__tier-dot" style={{ background: theme.swatchHex }} />
                        {tier.name}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {CANONICAL_BENEFITS.map((benefit) => (
                <tr key={benefit.label}>
                  <td>
                    <span className="membership-benefits__benefit-cell">
                      <span className="membership-benefits__benefit-icon" aria-hidden="true">{benefit.icon}</span>
                      {benefit.label}
                    </span>
                  </td>
                  {orderedTiers.map((tier) => {
                    const perks = parsePerks(tier.perksJson);
                    const isPointsBenefit = benefit.label === 'Points multiplier';
                    const included = includesBenefit(perks, benefit.label);
                    const value = isPointsBenefit
                      ? (tier.pointsEnabled ? formatPointsMultiplier(tier.pointsEarnRatePaisePerPoint) : '1×')
                      : (included ? 'check' : '—');
                    const dataAttr = value === 'check' ? 'check' : value === '—' ? 'dash' : 'value';
                    return (
                      <td
                        key={`${tier.membershipTypeUuid}-${benefit.label}`}
                        data-benefit-value={dataAttr}
                      >
                        {value === 'check' ? (
                          <span className="membership-benefits__check-icon" aria-label="Included"><Check size={14} /></span>
                        ) : value === '—' ? (
                          <span className="membership-benefits__dash-icon" aria-label="Not included">—</span>
                        ) : (
                          value
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </article>
  );
};

export default MembershipBenefitsTable;
